import React, { useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { Users, Plus, Edit, Trash2, Building2, User, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Teams = () => {
  const { teams, users, departments, updateUser, createTeam, deleteTeam, updateTeam } = useData();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [newTeam, setNewTeam] = useState({
    name: '',
    managerId: '',
    departmentId: '',
    teamMembers: []
  });

  // Function to create new team
  const handleCreateTeam = async () => {
    if (!newTeam.name || !newTeam.managerId || !newTeam.departmentId) {
      alert('אנא מלא את כל השדות הנדרשים');
      return;
    }
    
    try {
      // Create the team
      const teamId = await createTeam({
        name: newTeam.name,
        managerId: newTeam.managerId,
        departmentId: newTeam.departmentId,
        description: '',
        isActive: true
      });
      
      // Add team members to the team (using Promise.all for better performance)
      const updatePromises = [];
      
      // Add all team members
      for (const memberId of newTeam.teamMembers) {
        updatePromises.push(
          updateUser(memberId, { 
            teamId: teamId,
            teamManagerId: newTeam.managerId 
          })
        );
      }
      
      // Also add the manager to the team
      updatePromises.push(
        updateUser(newTeam.managerId, { 
          teamId: teamId,
          teamManagerId: newTeam.managerId 
        })
      );
      
      // Execute all updates in parallel
      await Promise.all(updatePromises);
      
      alert('הצוות נוצר בהצלחה!');
      
      // Close modal and reset
      setShowCreateTeamModal(false);
      setNewTeam({
        name: '',
        managerId: '',
        departmentId: '',
        teamMembers: []
      });
      
    } catch (error) {
      console.error('Error creating team:', error);
      alert('שגיאה ביצירת הצוות');
    }
  };

  // Get team managers (users with org_admin, ceo, deputy_ceo, or team_manager role)
  const teamManagers = users.filter(u => 
    u.role === 'org_admin' || 
    u.role === 'ceo' || 
    u.role === 'deputy_ceo' || 
    u.role === 'team_manager'
  );

  // Get employees from selected department (users who are not managers and not already in a team)
  const departmentEmployees = users.filter(u => 
    u.departmentId === newTeam.departmentId && 
    !['org_admin', 'ceo', 'deputy_ceo', 'team_manager'].includes(u.role) && 
    !u.teamId
  );

  // Check if user has access to teams
  if (!currentUser || !['org_admin', 'ceo', 'deputy_ceo', 'dept_manager', 'team_manager'].includes(currentUser.role)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">גישה נדחתה</h1>
          <p className="text-gray-600">אין לך הרשאות לצפות בעמוד זה</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">ניהול צוותים</h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">ניהול צוותים ומשתמשים בארגון</p>
        </div>
        <button
          onClick={() => setShowCreateTeamModal(true)}
          className="bg-orange-600 text-white px-4 py-3 rounded-lg hover:bg-orange-700 flex items-center justify-center space-x-2 space-x-reverse min-h-[44px] touch-manipulation"
        >
          <Plus className="w-4 h-4" />
          <span>צור צוות חדש</span>
        </button>
      </div>

      {/* Teams Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg flex-shrink-0">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600">סה"כ צוותים</p>
              <p className="text-2xl font-bold text-gray-900">{teams.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg flex-shrink-0">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600">צוותים פעילים</p>
              <p className="text-2xl font-bold text-gray-900">{teams.filter(t => t.isActive).length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg flex-shrink-0">
              <User className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600">עובדים בצוותים</p>
              <p className="text-2xl font-bold text-gray-900">{users.filter(u => (u.teamIds && u.teamIds.length > 0) || u.teamId).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Teams Cards */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900">רשימת צוותים</h2>
        </div>
        
        {teams.filter(team => {
          // Filter teams based on user role
          if (currentUser.role === 'team_manager') {
            // Team manager sees only their own team
            return team.managerId === currentUser.id;
          } else if (currentUser.role === 'dept_manager') {
            // Department manager sees teams in their department
            return team.departmentId === currentUser.departmentId;
          } else {
            // org_admin, ceo, deputy_ceo see all teams
            return true;
          }
        }).length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <p className="text-lg font-medium mb-2">אין צוותים מוגדרים עדיין</p>
            <p className="text-sm">צוותים יוצרים אוטומטית כאשר עובדים מוקצים לפרויקטים</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.filter(team => {
              // Filter teams based on user role
              if (currentUser.role === 'team_manager') {
                return team.managerId === currentUser.id;
              } else if (currentUser.role === 'dept_manager') {
                return team.departmentId === currentUser.departmentId;
              } else {
                return true;
              }
            }).map((team) => {
              const teamManager = users.find(u => u.id === team.managerId);
              const teamMembers = users.filter(u => (u.teamIds && u.teamIds.includes(team.id)) || u.teamId === team.id);
              const teamDepartment = departments.find(d => d.id === team.departmentId);
              
              return (
                <div 
                  key={team.id} 
                  className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                  onClick={() => navigate(`/teams/${team.id}`)}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                        {team.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {team.description || 'אין תיאור זמין'}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      team.isActive 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                      {team.isActive ? 'פעיל' : 'לא פעיל'}
                    </span>
                  </div>

                  {/* Department */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Building2 size={16} className="text-gray-400" />
                      <span className="font-medium">מחלקה:</span>
                      <span>{teamDepartment?.name || 'לא מוגדרת'}</span>
                    </div>
                  </div>

                  {/* Manager */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User size={16} className="text-gray-400" />
                      <span className="font-medium">מנהל צוות:</span>
                      <span>{teamManager?.name || 'לא מוגדר'}</span>
                    </div>
                  </div>

                  {/* Team Members Count */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Users size={16} className="text-gray-400" />
                      <span className="font-medium">חברי צוות:</span>
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                        {teamMembers.length} עובדים
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 pt-4 border-t border-blue-200" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => navigate(`/teams/${team.id}`)}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                      title="צפייה בפרטי הצוות"
                    >
                      <Eye size={14} />
                      צפייה
                    </button>
                    <button
                      onClick={() => {
                        setEditingTeam(team);
                        setShowEditTeamModal(true);
                      }}
                      className="bg-gray-100 text-gray-700 p-2 rounded-lg hover:bg-gray-200 transition-colors"
                      title="ערוך צוות"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('האם אתה בטוח שברצונך למחוק את הצוות הזה?')) {
                          deleteTeam(team.id);
                        }
                      }}
                      className="bg-red-100 text-red-600 p-2 rounded-lg hover:bg-red-200 transition-colors"
                      title="מחק צוות"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

             {/* Create Team Modal */}
       {showCreateTeamModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden">
             <div className="bg-orange-600 px-6 py-4">
               <h3 className="text-lg font-semibold text-white">צור צוות חדש</h3>
             </div>
             
             <div className="p-6">
               <div className="space-y-4">
                 {/* Team Name */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">שם הצוות *</label>
                   <input
                     type="text"
                     value={newTeam.name}
                     onChange={(e) => setNewTeam({...newTeam, name: e.target.value})}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                     placeholder="הכנס שם הצוות"
                     required
                   />
                 </div>
                 
                 {/* Team Manager */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">מנהל הצוות *</label>
                   {teamManagers.length > 0 ? (
                     <select
                       value={newTeam.managerId}
                       onChange={(e) => setNewTeam({...newTeam, managerId: e.target.value})}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                       required
                     >
                       <option value="">בחר מנהל צוות</option>
                       {teamManagers.map(manager => (
                         <option key={manager.id} value={manager.id}>
                           {manager.name}
                         </option>
                       ))}
                     </select>
                   ) : (
                     <div className="text-sm text-red-500 p-3 bg-red-50 rounded-lg">
                       אין מנהלי צוותים זמינים במערכת
                     </div>
                   )}
                 </div>
                 
                 {/* Department */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">שיוך למחלקה *</label>
                   {departments.length > 0 ? (
                     <select
                       value={newTeam.departmentId}
                       onChange={(e) => setNewTeam({...newTeam, departmentId: e.target.value, teamMembers: []})}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                       required
                     >
                       <option value="">בחר מחלקה</option>
                       {departments.map(dept => (
                         <option key={dept.id} value={dept.id}>{dept.name}</option>
                       ))}
                     </select>
                   ) : (
                     <div className="text-sm text-red-500 p-3 bg-red-50 rounded-lg">
                       אין מחלקות זמינות במערכת
                     </div>
                   )}
                 </div>
                 
                 {/* Team Members */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">הוספת חברי צוות (אופציונלי)</label>
                   {departmentEmployees.length > 0 ? (
                     <>
                       <select
                         multiple
                         value={newTeam.teamMembers}
                         onChange={(e) => {
                           const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                           setNewTeam({...newTeam, teamMembers: selectedOptions});
                         }}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-h-[100px]"
                       >
                         {departmentEmployees.map(employee => (
                           <option key={employee.id} value={employee.id}>
                             {employee.name}
                           </option>
                         ))}
                       </select>
                       <p className="text-xs text-gray-500 mt-1">
                         החזק Ctrl (או Cmd במק) כדי לבחור מספר עובדים
                       </p>
                     </>
                   ) : (
                     <div className="text-sm text-gray-500 p-3 bg-gray-100 rounded-lg">
                       {newTeam.departmentId 
                         ? 'אין עובדים זמינים במחלקה זו או שכולם כבר משויכים לצוותים'
                         : 'בחר מחלקה תחילה כדי לראות עובדים זמינים'
                       }
                     </div>
                   )}
                 </div>
               </div>
               
               <div className="flex justify-end gap-3 mt-6">
                 <button
                   onClick={() => setShowCreateTeamModal(false)}
                   className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                 >
                   ביטול
                 </button>
                 <button
                   onClick={handleCreateTeam}
                   disabled={!newTeam.name || !newTeam.managerId || !newTeam.departmentId}
                   className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   צור צוות
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Edit Team Modal */}
       {showEditTeamModal && editingTeam && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden">
             <div className="bg-blue-600 px-6 py-4">
               <h3 className="text-lg font-semibold text-white">ערוך צוות</h3>
             </div>
             
             <div className="p-6">
               <div className="space-y-4">
                 {/* Team Name */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">שם הצוות *</label>
                   <input
                     type="text"
                     value={editingTeam.name}
                     onChange={(e) => setEditingTeam({...editingTeam, name: e.target.value})}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                     placeholder="הכנס שם הצוות"
                     required
                   />
                 </div>
                 
                 {/* Team Description */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">תיאור הצוות</label>
                   <textarea
                     value={editingTeam.description || ''}
                     onChange={(e) => setEditingTeam({...editingTeam, description: e.target.value})}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                     placeholder="הכנס תיאור הצוות"
                     rows="3"
                   />
                 </div>
                 
                 {/* Team Manager */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">מנהל צוות *</label>
                   {teamManagers.length > 0 ? (
                     <select
                       value={editingTeam.managerId}
                       onChange={(e) => setEditingTeam({...editingTeam, managerId: e.target.value})}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       required
                     >
                       <option value="">בחר מנהל צוות</option>
                       {teamManagers.map(manager => (
                         <option key={manager.id} value={manager.id}>
                           {manager.name}
                         </option>
                       ))}
                     </select>
                   ) : (
                     <div className="text-sm text-red-500 p-3 bg-red-50 rounded-lg">
                       אין מנהלי צוותים זמינים במערכת
                     </div>
                   )}
                 </div>
                 
                 {/* Department */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">שיוך למחלקה *</label>
                   {departments.length > 0 ? (
                     <select
                       value={editingTeam.departmentId}
                       onChange={(e) => setEditingTeam({...editingTeam, departmentId: e.target.value})}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       required
                     >
                       <option value="">בחר מחלקה</option>
                       {departments.map(dept => (
                         <option key={dept.id} value={dept.id}>{dept.name}</option>
                       ))}
                     </select>
                   ) : (
                     <div className="text-sm text-red-500 p-3 bg-gray-100 rounded-lg">
                       אין מחלקות זמינות במערכת
                     </div>
                   )}
                 </div>
                 
                 {/* Active Status */}
                 <div>
                   <label className="flex items-center">
                     <input
                       type="checkbox"
                       checked={editingTeam.isActive !== false}
                       onChange={(e) => setEditingTeam({...editingTeam, isActive: e.target.checked})}
                       className="ml-2"
                     />
                     <span className="text-sm font-medium text-gray-700">צוות פעיל</span>
                   </label>
                 </div>
               </div>
               
               <div className="flex justify-end gap-3 mt-6">
                 <button
                   onClick={() => {
                     setShowEditTeamModal(false);
                     setEditingTeam(null);
                   }}
                   className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                 >
                   ביטול
                 </button>
                 <button
                   onClick={async () => {
                     try {
                       await updateTeam(editingTeam.id, editingTeam);
                       alert('הצוות עודכן בהצלחה!');
                       setShowEditTeamModal(false);
                       setEditingTeam(null);
                     } catch (error) {
                       console.error('Error updating team:', error);
                       alert('שגיאה בעדכון הצוות');
                     }
                   }}
                   disabled={!editingTeam.name || !editingTeam.managerId || !editingTeam.departmentId}
                   className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   שמור שינויים
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}
    </div>
  );
};

export default Teams;
